import { useState, useEffect, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Search, Pencil, Trash2, Users, Shield, GraduationCap, Eye, Heart, Camera, Link2, LogIn } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function timeAgo(dateStr) {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}
const ROLES = [
  { value: "student", label: "Student", icon: GraduationCap, color: "#5C3A2E" },
  { value: "instructor", label: "Instructor", icon: Shield, color: "#C6A969" },
  { value: "guardian", label: "Guardian", icon: Heart, color: "#5C3A2E" },
  { value: "spectator", label: "Spectator", icon: Eye, color: "#888" },
  { value: "custom", label: "Custom", icon: Users, color: "#C6A969" },
];

function RoleBadge({ role, customLabel }) {
  const cfg = ROLES.find((r) => r.value === role) || ROLES[4];
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider" style={{ backgroundColor: `${cfg.color}15`, color: cfg.color }}>
      {role === "custom" && customLabel ? customLabel : cfg.label}
    </span>
  );
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [editUser, setEditUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkingGuardian, setLinkingGuardian] = useState(null);
  const [form, setForm] = useState({ name: "", role: "student", course_id: "none", group_id: "none", custom_role_label: "", custom_role_id: "", address: "" });
  const [customRoles, setCustomRoles] = useState([]);

  const load = useCallback(async () => {
    const [u, c, g, cr] = await Promise.all([
      api.get("/users", { params: { search: search || undefined, role: filterRole === "all" ? undefined : filterRole } }),
      api.get("/courses"),
      api.get("/groups"),
      api.get("/custom-roles").catch(() => ({ data: [] })),
    ]);
    setUsers(u.data);
    setCourses(c.data);
    setGroups(g.data);
    setCustomRoles(cr.data);
    const s = await api.get("/users", { params: { role: "student" } });
    setStudents(s.data);
  }, [search, filterRole]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (u) => {
    setEditUser(u);
    setForm({
      name: u.name || "",
      role: u.role || "student",
      course_id: u.course_id || "none",
      group_id: u.group_id || "none",
      course_ids: u.course_ids || [],
      group_ids: u.group_ids || [],
      custom_role_label: u.custom_role_label || "",
      custom_role_id: u.custom_role_id || "",
      address: u.address || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const updates = { name: form.name, role: form.role };
      if (form.role === "student") {
        updates.course_id = form.course_id === "none" ? null : form.course_id;
        updates.group_id = form.group_id === "none" ? null : form.group_id;
      }
      if (form.role === "instructor") {
        updates.course_ids = form.course_ids;
        updates.group_ids = form.group_ids;
      }
      if (form.role === "custom") {
        updates.custom_role_label = form.custom_role_label;
        updates.custom_role_id = form.custom_role_id || null;
        updates.course_ids = form.course_ids;
        updates.group_ids = form.group_ids;
      }
      updates.address = form.address || null;
      await api.put(`/users/${editUser.id}`, updates);
      toast.success("User updated");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete ${u.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`${u.name} deleted`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleAvatarUpload = async (userId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put(`/users/${userId}`, { avatar_url: data.storage_path });
      toast.success("Photo updated");
      load();
    } catch { toast.error("Upload failed"); }
  };

  // Guardian linking
  const openLinkDialog = (guardian) => {
    setLinkingGuardian(guardian);
    setLinkDialogOpen(true);
  };

  const toggleStudentLink = async (studentId) => {
    if (!linkingGuardian) return;
    const current = linkingGuardian.linked_student_ids || [];
    const newList = current.includes(studentId)
      ? current.filter((id) => id !== studentId)
      : [...current, studentId];
    try {
      await api.put(`/users/${linkingGuardian.id}`, { linked_student_ids: newList });
      toast.success("Links updated");
      setLinkingGuardian({ ...linkingGuardian, linked_student_ids: newList });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const getGroupName = (id) => groups.find((g) => g.id === id)?.name || "";
  const getStudentName = (id) => students.find((s) => s.id === id)?.name || id;
  const filteredGroups = form.course_id && form.course_id !== "none" ? groups.filter((g) => g.course_id === form.course_id) : groups;

  return (
    <AdminLayout>
      <div data-testid="admin-users" className="space-y-6 animate-fade-in-up">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-[#5C3A2E] font-semibold tracking-tight">Users</h1>
          <p className="text-[#2B2B2B]/50 mt-1 text-sm">{users.length} users · Manage roles, permissions, and links</p>
        </div>

        {/* Role Filter Tabs */}
        <div className="flex gap-2 flex-wrap">
          {[{ value: "all", label: "All" }, ...ROLES].map((r) => (
            <button
              key={r.value}
              data-testid={`filter-role-${r.value}`}
              onClick={() => setFilterRole(r.value)}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                filterRole === r.value
                  ? "bg-[#5C3A2E] text-white"
                  : "bg-[#EADBC8]/30 text-[#2B2B2B]/60 hover:bg-[#EADBC8]/50"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#2B2B2B]/30" />
          <Input
            data-testid="user-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email..."
            className="pl-10 rounded-xl border-[#EADBC8]"
          />
        </div>

        {/* Users List */}
        <div className="grid gap-3">
          {users.map((u) => {
            const avatarUrl = u.avatar_url ? `${API_BASE}/api/files/${u.avatar_url}` : null;
            const isGuardian = u.role === "guardian";
            const linkedCount = (u.linked_student_ids || []).length;
            return (
              <div key={u.id} data-testid={`user-card-${u.id}`} className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-4 sm:p-5 studio-card">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    {/* Avatar with upload */}
                    <label className="relative w-10 h-10 rounded-full overflow-hidden bg-[#EADBC8] flex items-center justify-center flex-shrink-0 cursor-pointer group">
                      {avatarUrl ? (
                        <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-sm font-semibold text-[#5C3A2E]">{u.name?.[0]?.toUpperCase() || "?"}</span>
                      )}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                        <Camera className="w-3.5 h-3.5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <input type="file" accept="image/*" onChange={(e) => handleAvatarUpload(u.id, e)} className="hidden" />
                    </label>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-[#2B2B2B] text-sm truncate">{u.name}</h4>
                        <RoleBadge role={u.role} customLabel={u.custom_role_label} />
                      </div>
                      <p className="text-xs text-[#2B2B2B]/40 truncate">{u.email}</p>
                      <div className="flex items-center gap-3 mt-0.5 text-[10px] text-[#2B2B2B]/30">
                        {u.group_id && <span>{getGroupName(u.group_id)}</span>}
                        {(u.group_ids || []).length > 0 && <span>{u.group_ids.map(getGroupName).filter(Boolean).join(", ")}</span>}
                        {isGuardian && <span className="text-[#C6A969]">{linkedCount} linked student{linkedCount !== 1 ? "s" : ""}</span>}
                        <span className="flex items-center gap-0.5">
                          <LogIn className="w-2.5 h-2.5" />
                          {timeAgo(u.last_login_at) || "Never logged in"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {isGuardian && (
                      <Button data-testid={`link-guardian-${u.id}`} variant="ghost" size="sm" onClick={() => openLinkDialog(u)} title="Link students" className="text-[#C6A969] hover:bg-[#C6A969]/10 rounded-full h-8 w-8 p-0">
                        <Link2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    <Button data-testid={`edit-user-${u.id}`} variant="ghost" size="sm" onClick={() => openEdit(u)} className="text-[#5C3A2E] hover:bg-[#EADBC8]/30 rounded-full h-8 w-8 p-0">
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button data-testid={`delete-user-${u.id}`} variant="ghost" size="sm" onClick={() => handleDelete(u)} className="text-red-400 hover:bg-red-50 rounded-full h-8 w-8 p-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
          {users.length === 0 && (
            <div className="text-center py-12">
              <Users className="w-10 h-10 text-[#EADBC8] mx-auto mb-3" />
              <p className="text-[#2B2B2B]/40 text-sm">No users found</p>
            </div>
          )}
        </div>

        {/* Edit User Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-white rounded-xl max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#5C3A2E]">Edit User</DialogTitle>
              <DialogDescription className="text-[#2B2B2B]/50 text-sm">{editUser?.email}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Name</label>
                <Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl border-[#EADBC8]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Role</label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role-select" className="rounded-xl border-[#EADBC8]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {form.role === "custom" && (
                <>
                  <div>
                    <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Custom Role</label>
                    <Select value={form.custom_role_id || "none"} onValueChange={(v) => {
                      const role = customRoles.find((r) => r.id === v);
                      setForm({ ...form, custom_role_id: v === "none" ? "" : v, custom_role_label: role?.name || form.custom_role_label });
                    }}>
                      <SelectTrigger className="rounded-xl border-[#EADBC8]"><SelectValue placeholder="Select role" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No role assigned</SelectItem>
                        {customRoles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Display Label</label>
                    <Input value={form.custom_role_label} onChange={(e) => setForm({ ...form, custom_role_label: e.target.value })} placeholder="e.g., Kalemash Support" className="rounded-xl border-[#EADBC8]" />
                  </div>
                </>
              )}
              {form.role === "student" && (
                <>
                  <div>
                    <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Course</label>
                    <Select value={form.course_id} onValueChange={(v) => setForm({ ...form, course_id: v, group_id: "none" })}>
                      <SelectTrigger className="rounded-xl border-[#EADBC8]"><SelectValue placeholder="Select course" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Group</label>
                    <Select value={form.group_id} onValueChange={(v) => setForm({ ...form, group_id: v })}>
                      <SelectTrigger className="rounded-xl border-[#EADBC8]"><SelectValue placeholder="Select group" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {filteredGroups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              {(form.role === "instructor" || form.role === "custom") && (
                <>
                  <div>
                    <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Assigned Courses</label>
                    <div className="space-y-1 max-h-32 overflow-y-auto border border-[#EADBC8] rounded-xl p-2">
                      {courses.map((c) => (
                        <label key={c.id} className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-[#F7F3EF] cursor-pointer">
                          <input type="checkbox" checked={(form.course_ids || []).includes(c.id)}
                            onChange={(e) => {
                              const ids = form.course_ids || [];
                              setForm({ ...form, course_ids: e.target.checked ? [...ids, c.id] : ids.filter((x) => x !== c.id) });
                            }}
                            className="rounded border-[#EADBC8] text-[#5C3A2E] focus:ring-[#C6A969]" />
                          <span className="text-sm text-[#2B2B2B]">{c.title}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Assigned Groups</label>
                    <div className="space-y-1 max-h-32 overflow-y-auto border border-[#EADBC8] rounded-xl p-2">
                      {groups.map((g) => (
                        <label key={g.id} className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-[#F7F3EF] cursor-pointer">
                          <input type="checkbox" checked={(form.group_ids || []).includes(g.id)}
                            onChange={(e) => {
                              const ids = form.group_ids || [];
                              setForm({ ...form, group_ids: e.target.checked ? [...ids, g.id] : ids.filter((x) => x !== g.id) });
                            }}
                            className="rounded border-[#EADBC8] text-[#5C3A2E] focus:ring-[#C6A969]" />
                          <span className="text-sm text-[#2B2B2B]">{g.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Address</label>
                <textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} placeholder="Address for invoicing" className="w-full rounded-xl border border-[#EADBC8] bg-white px-4 py-2.5 text-sm text-[#2B2B2B] focus:ring-2 focus:ring-[#C6A969] focus:border-transparent resize-none" />
              </div>
              <Button data-testid="save-user-button" onClick={handleSave} className="w-full bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90 h-11">
                Save Changes
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Guardian Link Dialog */}
        <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
          <DialogContent className="bg-white rounded-xl max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#5C3A2E]">Link Students</DialogTitle>
              <DialogDescription className="text-[#2B2B2B]/50 text-sm">
                Select students that {linkingGuardian?.name} can view
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 mt-2 max-h-64 overflow-y-auto">
              {students.map((s) => {
                const linked = (linkingGuardian?.linked_student_ids || []).includes(s.id);
                return (
                  <label key={s.id} className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${linked ? "bg-[#5C3A2E]/5 border border-[#5C3A2E]/20" : "hover:bg-[#F7F3EF] border border-transparent"}`}>
                    <input
                      type="checkbox"
                      checked={linked}
                      onChange={() => toggleStudentLink(s.id)}
                      className="rounded border-[#EADBC8] text-[#5C3A2E] focus:ring-[#C6A969]"
                    />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-[#2B2B2B]">{s.name}</span>
                      <p className="text-xs text-[#2B2B2B]/40">{s.email}</p>
                    </div>
                    {linked && <Link2 className="w-4 h-4 text-[#C6A969]" />}
                  </label>
                );
              })}
              {students.length === 0 && <p className="text-sm text-[#2B2B2B]/40 text-center py-4">No students to link</p>}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
